import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import {
  getDraftQuestion,
  getQuestionDetail,
  patchEditQuestion,
  postSaveQuestionAsDraft,
} from '../../../services/MyQuestions/QuestionService';
import QuestionCard from '../CreateQuestion/Questions/QuestionCard';
import {
  getObjectives,
  getPurposes,
} from '../../../services/questionService';
import questionReducer, {
  cardHandlers,
  getQuestionWarnings,
  getSaveErrorMessage,
  incompleteByIndex,
  missingErrors,
  scrollToFirstInvalid,
  validateQuestions,
} from '../CreateQuestion/Questions/QuestionUtils';
import {
  EditUnavailable,
  NotSavedNotice,
  PrintedPassageNotice,
  PrintedQuestion,
  SAVE_REFUSALS,
  StaleNotice,
  bookletRefusal,
  editBlockReason,
  loadFailure,
  saveRefusalMessage,
  useEditBackLinks,
} from '../CreateQuestion/Questions/EditNotices';
import FormHeader from '../CreateQuestion/FormHeader';
import { Button } from '../../../components/forms/Buttons/Button';
import { toast } from 'react-toastify';
import { draftErrorCode, draftFailureMessage, failedDrafts } from '../../../utils/draftSave';
import SimpleLoader from '../../../components/Loader/SimpleLoader';
import PassagePanel from '../../../components/Passages/PassagePanel';
import { passageBooklets } from '../../../components/Passages/PassageLock';
import usePassage, { forgetPassage } from '../../../components/Passages/usePassage';
import { UserContext } from '../../../utils/contexts/UserContext';
import { isSubjectFolder } from '../../../utils/folders';
import { getApiErrorCode } from '../../../utils/apiError';
import { BookletLinks, bookletLockKind, questionBooklets } from '../../QuestionPage/review/bookletLock';
import { changedFields, editsToRestore } from './staleEdits';

const listOf = (res) => (Array.isArray(res) ? res : res?.results || []);

// The draft as stored and sent: a passage_uuid cleared by "Unlink from passage" is left out.
const withoutClearedPassage = (question) => {
  if (question.passage_uuid) return question;
  const { passage_uuid, ...rest } = question;
  return rest;
};

// The draft as the form edits it (get_draft returns the data as saved).
const formDraft = (uuid, draft) => {
  const data = draft && typeof draft === 'object' ? draft : {};
  return {
    uuid,
    ...data,
    id: 0,
    body: data.body ?? '',
    answer_keys: Array.isArray(data.answer_keys) ? data.answer_keys : [],
    topic: data.topic,
    // get_draft now returns topic_name (null if the topic is gone): QuestionCard prefills the topic picker.
    topic_name: data.topic_name || '',
    objective: data.objective,
    subtopic: data.subtopic,
    purpose: data.purpose,
  };
};

export default function EditDraftQuestion() {
  const [t] = useTranslation();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const navState = useLocation().state;
  const { user } = useContext(UserContext) || {};
  const backLinks = useEditBackLinks(uuid, { isDraft: true });
  const [purposes, setPurposes] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [questions, dispatch] = useReducer(questionReducer, []);
  const [isUnlinking, setIsUnlinking] = useState(false);
  // The question's detail (null when it could not be read): its folder, author, `actions` and `version`.
  const [detail, setDetail] = useState(null);
  const [load, setLoad] = useState({ loading: true, error: null });
  const [attempt, setAttempt] = useState(0);
  // The version the submit starts from, sent as base_version (undefined: a backend without versions).
  const [baseVersion, setBaseVersion] = useState(undefined);
  const [formKey, setFormKey] = useState(0);
  // The draft as opened: what the user changed since is kept over a 409 stale_version.
  const openedRef = useRef(null);
  // After a 409 stale_version: {edits}, the user's changes the reload replaced (null: none); {restored} once they
  // are back in the form.
  const [stale, setStale] = useState(null);
  // A save refused for the question's stage or owner (edit_locked, draft_not_own, not_a_draft).
  const [refusal, setRefusal] = useState(null);
  // A submit refused because an active BTS booklet prints the passage the draft joins, or the question (409
  // question_in_booklet): {message, booklets, key}, said above the form, which keeps the user's changes (`booklets`:
  // their links, for an admin).
  const [bookletLock, setBookletLock] = useState(null);

  const inSubject = isSubjectFolder(detail?.folder);

  // Opens what was loaded: false when the question is no longer a draft (sent to review meanwhile, or an
  // old link), which is then edited itself, in its own editor. `staleSubmit`: a submit of this page refused as
  // stale ({draftSent, edits}), which that editor reports, with the user's changes to put back.
  const openDraft = useCallback(
    (detailRes, draftRes, staleSubmit = null) => {
      if (detailRes?.status && detailRes.status.code !== 'DRAFT') {
        const state = staleSubmit ? { ...navState, stale: staleSubmit } : navState;
        navigate(`/my-questions/edit/${uuid}`, { replace: true, state });
        return false;
      }
      openedRef.current = formDraft(uuid, draftRes?.draft);
      // The passage's detail says whether a booklet prints it now (the submit waits while one does): read afresh.
      if (openedRef.current.passage_uuid) forgetPassage(openedRef.current.passage_uuid);
      dispatch({ type: 'RESET', questions: [openedRef.current] });
      setDetail(detailRes);
      setBaseVersion(detailRes && 'version' in detailRes ? detailRes.version : undefined);
      setErrors({});
      setFormKey((key) => key + 1);
      return true;
    },
    [navigate, navState, uuid],
  );

  // The draft and the question's detail. The detail guards the page (actions.edit) and gives the submit its
  // version; without it (an older backend) the draft alone opens.
  const fetchDraft = useCallback(
    (signal) =>
      Promise.all([
        getQuestionDetail(uuid, { signal }).catch(() => null),
        getDraftQuestion({ params: { uuid }, signal }),
      ]),
    [uuid],
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setLoad({ loading: true, error: null });
    Promise.all([
      getPurposes({ params: {}, signal }).catch(() => []),
      getObjectives({ params: {}, signal }).catch(() => []),
      fetchDraft(signal),
    ])
      .then(([puroposesRes, objectivesRes, [detailRes, draftRes]]) => {
        if (signal.aborted) return;
        setPurposes(listOf(puroposesRes));
        setObjectives(listOf(objectivesRes));
        if (openDraft(detailRes, draftRes)) setLoad({ loading: false, error: null });
      })
      .catch((error) => {
        if (!signal.aborted) setLoad({ loading: false, error });
      });

    return () => {
      controller.abort();
    };
  }, [attempt, fetchDraft, openDraft]);

  // After a 409 stale_version: the draft and the question as they are now replace the user's changes, which are
  // kept to be put back (`edits`). A draft sent to review meanwhile (from another tab: a draft's versioned change
  // is its submit) goes on in the question's own editor, which says so.
  const reloadDraft = (edits) => {
    setLoad({ loading: true, error: null });
    fetchDraft()
      .then(([detailRes, draftRes]) => {
        if (!openDraft(detailRes, draftRes, { draftSent: true, edits })) {
          toast.error(t('my_questions.edit_guard.not_saved_title'));
          return;
        }
        setStale({ edits });
        setLoad({ loading: false, error: null });
      })
      .catch((error) => setLoad({ loading: false, error }));
  };

  // "Restore my changes": the user's changes a 409 replaced, back in the form (the draft as loaded stays the base).
  const restoreEdits = (edits) => {
    dispatch({ type: 'RESET', questions: [{ ...questions[0], ...edits }] });
    setErrors({});
    setFormKey((key) => key + 1);
    setStale({ restored: true });
  };

  // A draft written for a passage keeps only passage_uuid in its data (get_draft returns the data as saved):
  // the panel takes the name from the passage itself.
  const { data: draftPassage, error: passageError } = usePassage(questions[0]?.passage_uuid || null);
  // The passage was deleted after the draft was written: the server refuses to submit the draft with it.
  const passageGone = passageError?.response?.status === 404;

  // A refused save_draft: its reason, and for a refusal of the question's stage or owner the notice too.
  const draftRefused = (failed) => {
    const code = draftErrorCode(failed[0]);
    if (SAVE_REFUSALS.includes(code)) setRefusal(code);
    toast.error(draftFailureMessage(t, failed, 1));
  };

  // Takes passage_uuid out of the stored draft; the question is then submitted without a passage.
  const unlinkPassage = () => {
    setIsUnlinking(true);
    const { passage_uuid, ...draft } = questions[0];
    postSaveQuestionAsDraft({ draft, uuid })
      .then((results) => {
        // A refused save (207 with status "error") leaves the draft linked.
        const failed = failedDrafts(results);
        if (failed.length) {
          draftRefused(failed);
          return;
        }
        dispatch({ type: 'UPDATE_QUESTION_FIELD', id: 0, field: 'passage_uuid', value: null });
        toast.success(t('passages.draft.unlinked'));
      })
      .catch((e) => {
        toast.error(getSaveErrorMessage(t, e));
      })
      .finally(() => {
        setIsUnlinking(false);
      });
  };

  const saveDraft = (e) => {
    if (e.detail > 1) return;
    setIsSavingDraft(true);
    postSaveQuestionAsDraft({ draft: withoutClearedPassage(questions[0]), uuid })
      .then((results) => {
        // A refused save (207 with status "error") keeps the page, with the reason.
        const failed = failedDrafts(results);
        if (failed.length) {
          draftRefused(failed);
          return;
        }
        toast.success(t('my_questions.toasts.saved_as_draft'));
        navigate('/my-questions');
      })
      .catch((err) => {
        toast.error(getSaveErrorMessage(t, err));
      })
      .finally(() => {
        setIsSavingDraft(false);
      });
  };

  // Sends the draft to review: in an exam's subject only with everything review needs.
  const submit = (e) => {
    if (e.detail > 1) return;
    const validation = validateQuestions(questions, { inSubject });
    setErrors(validation);
    if (validation[0]) {
      toast.error(t('my_questions.validation.error'));
      scrollToFirstInvalid();
      return;
    }
    const warns = getQuestionWarnings(questions, { inSubject });
    warns.forEach((w) => toast.warn(w));
    setIsSaving(true);
    // passage ({uuid, name}) is output-only: questions are linked from the passage itself.
    const { author, status, evaluation, passage, ...body } = withoutClearedPassage(questions[0]);
    patchEditQuestion(uuid, { ...body, ...(baseVersion !== undefined ? { base_version: baseVersion } : {}) })
      .then(() => {
        toast.success(t('my_questions.toasts.question_edited'));
        navigate('/my-questions');
      })
      .catch((err) => {
        const code = getApiErrorCode(err);
        const incomplete = incompleteByIndex(err);
        if (incomplete) {
          setErrors({ 0: missingErrors(incomplete[0], questions[0]) });
          toast.error(t('errors.incomplete'));
          scrollToFirstInvalid();
        } else if (code === 'stale_version') {
          reloadDraft(changedFields(openedRef.current, questions[0]));
        } else if (SAVE_REFUSALS.includes(code)) {
          setRefusal(code);
          toast.error(saveRefusalMessage(t, code));
        } else if (code === 'question_in_booklet') {
          // The refusal's `lock` says what holds it: "passage" (the passage the draft joins is printed: a submit that
          // joins one checks it first, and a draft is never printed itself) or "question". An older backend sends
          // none: then the draft joining a passage tells.
          const joining = !!body.passage_uuid && body.passage_uuid !== detail?.passage?.uuid;
          const kind = bookletLockKind(err) ?? (joining ? 'passage' : 'question');
          setBookletLock((prev) => ({ ...bookletRefusal(t, err, user, kind), key: (prev?.key || 0) + 1 }));
        } else {
          toast.error(getSaveErrorMessage(t, err));
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  if (load.loading) return <SimpleLoader className="h-10" />;

  if (load.error) {
    const failure = loadFailure(t, load.error);
    return (
      <EditUnavailable
        title={failure.title}
        message={failure.message}
        links={backLinks}
        onRetry={failure.retry ? () => setAttempt((n) => n + 1) : undefined}
      />
    );
  }

  // An active BTS booklet prints the question (a draft is not printed itself; kept as the question editor does): every
  // change is refused, so the page says why instead of a form whose save could only fail.
  const printedBy = questionBooklets(detail);
  if (printedBy.length) {
    return <PrintedQuestion question={detail} booklets={printedBy} links={backLinks} />;
  }

  // Someone else's draft (only its author changes and submits it), or no rights: no form.
  if (detail?.actions && detail.actions.edit === false) {
    return (
      <EditUnavailable
        locked
        title={t('my_questions.edit_guard.blocked_title')}
        message={editBlockReason(t, detail, user)}
        question={detail}
        links={backLinks}
      />
    );
  }

  const question = questions[0];
  const busy = isSaving || isSavingDraft || isUnlinking;
  const restorable = editsToRestore(stale?.edits, question);
  // The draft joins a passage (one it is not in yet) that an active BTS booklet prints: its submit can only be refused
  // (409 question_in_booklet, lock "passage"), so the submit waits, with the reason right above it; saving the draft
  // works.
  const joinsPassage = !!question?.passage_uuid && question.passage_uuid !== detail?.passage?.uuid;
  const passageLock = joinsPassage && draftPassage?.uuid === question.passage_uuid ? passageBooklets(draftPassage) : [];
  const passageLocked = passageLock.length > 0;
  // not_a_draft: the question went to review meanwhile; its changes are made in its own editor, which takes the
  // user's changes along, to put them back. (Without `from`: that editor's save would go back to this page.)
  const unsentEdits = refusal === 'not_a_draft' ? changedFields(openedRef.current, question) : null;
  const refusalLinks =
    refusal === 'not_a_draft'
      ? [
          {
            to: `/my-questions/edit/${uuid}`,
            label: t('my_questions.edit_guard.open_question_editor'),
            state: unsentEdits ? { stale: { draftSent: true, edits: unsentEdits } } : undefined,
          },
        ]
      : backLinks;

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4 motion-safe:animate-fade-in">
      <FormHeader
        title={t('my_questions.editor.edit_heading')}
        mode="draft"
        inSubject={inSubject}
        linked={!!question?.passage_uuid}
      />
      {stale && (
        <StaleNotice
          stale={stale}
          onRestore={restorable ? () => restoreEdits(restorable) : undefined}
          onClose={() => setStale(null)}
        />
      )}
      {refusal && <NotSavedNotice message={saveRefusalMessage(t, refusal)} links={refusalLinks} />}
      {!refusal && bookletLock && (
        <NotSavedNotice
          key={`booklet-${bookletLock.key}`}
          message={
            <>
              {bookletLock.message}
              <BookletLinks booklets={bookletLock.booklets} />
              <span className="mt-1 block">
                {t('passages.draft.save_later', { button: t('my_questions.actions.save_draft_and_exit') })}
              </span>
            </>
          }
          links={backLinks}
        />
      )}
      {/* The passage the question is based on: shown to write against, not edited here. */}
      <PassagePanel passage={draftPassage ? { uuid: draftPassage.uuid, name: draftPassage.name } : null} />
      {passageGone && (
        <div role="alert" className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-amber-900">{t('passages.draft.gone_title')}</p>
              <p className="text-sm text-amber-800">{t('passages.draft.gone_text')}</p>
            </div>
          </div>
          <Button
            text={t('passages.draft.unlink')}
            color="secondary-outline"
            loading={isUnlinking}
            disabled={busy || !!refusal}
            onClick={unlinkPassage}
            className="shrink-0 self-start max-sm:h-11 sm:self-auto"
          />
        </div>
      )}
      {question && (
        <QuestionCard
          key={formKey}
          isEdit
          required={inSubject}
          subject={question.subject_uuid || detail?.subject?.uuid}
          errors={errors[0]}
          question={question}
          purposes={purposes}
          objectives={objectives}
          {...cardHandlers(0, question, dispatch, setErrors)}
        />
      )}
      {/* Right above the button it holds back: the passage the draft joins is printed now. */}
      {!refusal && !bookletLock && passageLocked && (
        <PrintedPassageNotice booklets={passageLock} id="draft-passage-lock" />
      )}
      {/* Phones stack the two (44px), the main one on top; wider screens keep them in a row on the right. */}
      <div className="flex flex-col-reverse gap-2 pb-6 sm:flex-row sm:justify-end">
        <Button
          className="max-sm:h-11"
          text={t('my_questions.actions.save_draft_and_exit')}
          color="secondary-outline"
          loading={isSavingDraft}
          disabled={busy || !!refusal}
          onClick={saveDraft}
        />

        <Button
          className="max-sm:h-11"
          color="primary"
          text={t('my_questions.actions.finish_and_create')}
          disabled={busy || passageGone || passageLocked || !!refusal}
          loading={isSaving}
          onClick={submit}
          aria-describedby={passageLocked && !bookletLock && !refusal ? 'draft-passage-lock' : undefined}
        />
      </div>
    </div>
  );
}
